import { Link } from "react-router";
import { usePageMeta } from "../components/Layout.tsx";
import { useSession } from "../session.tsx";
import { BANK } from "../bank/index.ts";

export function Home() {
  usePageMeta(
    "Coding",
    "Ten minutes, one TypeScript problem a day, written from memory against hidden tests and scheduled to come back.",
  );
  const { user } = useSession();

  return (
    <>
      <section className="hero">
        <h1>AI writes it now. Keep your hands in the code.</h1>
        <p className="lede">
          I use AI every day, I enjoy it, and I am not going back. What it quietly took is the part
          that produces code from an empty file: the patterns I used to apply without thinking, and the
          problem solving underneath them. That muscle does not announce that it is fading. It just
          stops being there the day you need it.
        </p>
        <p className="lede">
          So this is ten minutes and one problem, on a schedule that brings each one back just before
          you would have forgotten it. It is not a LeetCode replacement and it is not trying to be. I
          did not want three thousand problems and I did not have the time to grind them.
        </p>
        <p className="lede">
          <strong>TypeScript only.</strong> Every problem is written in it and solved in it, and there
          are no plans to add another language. If you want to practise in Python or Go, this is not
          the tool.
        </p>
        <div className="cta">
          {user ? (
            <Link className="btn primary" to="/drill">
              Start today's drill
            </Link>
          ) : (
            <>
              {/* The first ask is the drill, not the account. Nobody opens an
                  account to find out whether a tool is any good, and the drill
                  is the only argument this page has that a paragraph is not. */}
              <Link className="btn primary" to="/drill">
                Try a drill, no account
              </Link>
              <Link className="btn" to="/login">
                Sign in
              </Link>
            </>
          )}
          <Link className="btn" to="/how-it-works">
            How it works
          </Link>
        </div>
      </section>

      <section className="demo">
        <h2>Why it exists, in under two minutes</h2>
        {/* preload="metadata" not "auto": the poster is what sells the page and
            4MB of video should not be spent on a visitor who never presses play.
            playsInline stops iOS from taking the video fullscreen on tap. */}
        <video
          controls
          preload="metadata"
          playsInline
          poster="/demo-poster.jpg"
          width={1280}
          height={840}
        >
          <source src="/demo.mp4" type="video/mp4" />
          Your browser cannot play this video. It is a screen recording of a drill being solved.
        </video>
      </section>

      <section className="grid-3">
        <article>
          <h3>Timed, like the real thing</h3>
          <p>
            Every problem has a budget. The clock keeps running past it rather than locking you out,
            because being cut off mid-thought teaches nothing. Going over costs you the grade, which is
            the honest penalty.
          </p>
        </article>
        <article>
          <h3>Paste is blocked</h3>
          <p>
            The whole value is producing the code from memory. You can defeat it with devtools if you
            want to, and that is fine. It is there to stop the reflex, not to police you.
          </p>
        </article>
        <article>
          <h3>Scheduled, not shuffled</h3>
          <p>
            Passing pushes a problem further out, failing brings it back in twenty minutes. Do as many
            as you like in a day: there is no cap, and extra reps count for half so a heavy session
            cannot fling everything months into the future.
          </p>
        </article>
      </section>

      <section className="strip">
        <div>
          <strong>{BANK.length}</strong>
          <span>{BANK.length === 1 ? "problem" : "problems"} in the bank</span>
        </div>
        <div>
          <strong>{new Set(BANK.map((d) => d.pattern)).size}</strong>
          <span>patterns covered</span>
        </div>
        <div>
          <strong>TypeScript</strong>
          <span>and only TypeScript</span>
        </div>
        <div>
          <strong>Free</strong>
          <span>and staying that way</span>
        </div>
      </section>

      <section className="honest">
        <h2>What this is not</h2>
        <p>
          It is not a replacement for LeetCode, and it is deliberately small. The bank is short and
          grows when a problem is worth adding rather than to hit a number. It is TypeScript only, with
          no multi-language support planned. There are no video walkthroughs, no discussion threads and
          no streak-shaming email. If you want a thousand problems in a language of your choice, this
          is the wrong tool.
        </p>
        <p>
          <Link to="/drills">See exactly what is in the bank</Link>, and open any of them without an
          account. The list is short and it is not hidden.
        </p>
        <p>
          <strong>What the account is for.</strong> The schedule, and only the schedule. Every problem
          is open to a signed-out visitor; what an account adds is that a pass pushes a problem weeks
          out and a fail brings it back in twenty minutes, which is the part that cannot live in a
          browser tab you are about to close.
        </p>
      </section>
    </>
  );
}
